import { Component, Input } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { formatDate } from '@telerik/kendo-intl';

import { MeetingDetailsDto, MeetingType, CategoryDto, SupporterEditDto, Language } from '../../../../shared/model';
import { SharedService } from '../../../../shared/shared.service';

@Component({
  selector: 'app-edit-meeting-view',
  templateUrl: './edit-meeting-view.component.html',
  styleUrls: [
    './edit-meeting-view.component.css',
    '../../../../shared/styles/edit-modal.css',
    '../../../../shared/styles/confirm-dialog.css',
    '../../../../shared/styles/translation.css']
})
export class EditMeetingViewComponent {

  @Input() supporters: SupporterEditDto[];
  @Input() meeting: MeetingDetailsDto;
  @Input() allMeetings: MeetingDetailsDto[];
  @Input() allCategories: CategoryDto[];

  myForm: FormGroup;
  cancelOpened = false;
  changeSupporterOpened = false;
  supportersDropdown: SupporterEditDto[];
  newSupporterId: string;
  oldSupporterId: string;
  supporterName: string;
  newCategoryId: string;
  oldCategoryId: string;
  selectedCategory: CategoryDto = <CategoryDto>{};
  selectedSupporter: SupporterEditDto = <SupporterEditDto>{};
  warningDisplayed = false;
  flags: any[];
  isEditable = false;
  discarded = false;
  translatedLanguages: Array<Language>;
  translatedMeetingLanguage: Language;

  constructor(
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private activeModal: NgbActiveModal) {
    this.createForm();
    this.flags = this.sharedService.getFlags();
    this.translatedLanguages = this.sharedService.getTranslatedLanguages();
  }

  createForm() {
    this.myForm = this.formBuilder.group({
      MeetingId: '',
      SupporterId: '',
      CategoryId: '',
      ToCancel: false,
      HasChanges: false
    });
  }

  setValues() {
    this.myForm.controls['MeetingId'].setValue(this.meeting.Id);
    this.oldSupporterId = this.meeting.SupporterId.toString();
    this.myForm.controls['SupporterId'].setValue(this.oldSupporterId);
    this.selectedSupporter = this.supporters.find(s => s.Id === this.oldSupporterId);
    this.supportersDropdown = this.supporters;
    this.oldCategoryId = this.meeting.CategoryId;
    this.myForm.controls['CategoryId'].setValue(this.oldCategoryId);
    this.selectedCategory = this.allCategories.find(c => c.Id === this.oldCategoryId);
    this.supporterName = this.meeting.SupporterFirstName.toString() + ' ' + this.meeting.SupporterLastName.toString();
    this.setIfMeetingIsEditable();
    this.filterSupportersByCategory(this.oldCategoryId);
    this.setMeetingLanguage();
  }

  submitForm() {
    if (this.changesDetected() && !this.checkIfSupporterHasMeetings() && !this.discarded) {
      this.myForm.controls['HasChanges'].setValue(true);
      this.activeModal.close(this.myForm.value);
      this.meeting = null;
    } else {
      this.myForm.controls['HasChanges'].setValue(false);
      this.closeForm();
    }
  }

  closeForm() {
    this.meeting = null;
    this.activeModal.dismiss();
  }

  changesDetected() {
    return this.checkIfSupporterIsChanged() || this.checkIfCategoryIsChanged();
  }

  checkIfSupporterIsChanged(): boolean {
    const newlySetSupporterId = this.myForm.controls['SupporterId'].value;
    return !!newlySetSupporterId && this.oldSupporterId !== newlySetSupporterId;
  }

  checkIfCategoryIsChanged(): boolean {
    const newlySetCategoryId = this.myForm.controls['CategoryId'].value;
    return !!newlySetCategoryId && this.oldCategoryId !== newlySetCategoryId;
  }

  openCancelMeeting() {
    this.cancelOpened = true;
  }

  cancelClose() {
    this.cancelOpened = false;
  }

  cancelConfirmed() {
    this.cancelClose();
    this.cancelMeeting();
  }

  cancelMeeting() {
    this.myForm.controls['ToCancel'].setValue(true);
    this.myForm.controls['HasChanges'].setValue(true);
    this.activeModal.close(this.myForm.value);
    this.meeting = null;
  }

  openChangeSupporter() {
    this.changeSupporterOpened = true;
  }

  closeChangeSupporter() {
    this.changeSupporterOpened = false;
  }

  discardChanges() {
    this.selectedCategory = this.allCategories.find(c => c.Id === this.oldCategoryId);
    this.selectedSupporter = this.supporters.find(s => s.Id === this.oldSupporterId);
    this.changeSupporterOpened = false;
    this.myForm.controls['HasChanges'].setValue(false);
    this.discarded = true;
  }

  changeSupporterConfirmed() {
    if (this.selectedCategory !== undefined && this.selectedSupporter !== undefined && !this.checkIfSupporterHasMeetings()) {
      if (!!this.newSupporterId) {
        this.myForm.controls['SupporterId'].setValue(this.newSupporterId);
        this.setSupporterName();
      }

      if (!!this.newCategoryId) {
        this.myForm.controls['CategoryId'].setValue(this.newCategoryId);
        this.setCategoryName();
      }
      this.closeChangeSupporter();
    }
  }

  handleFilter(value: string) {
    if (value.length >= 1) {
      this.supportersDropdown = this.supporters.filter((s) => s.Email.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    } else {
      this.supportersDropdown = this.supporters;
    }
  }

  assignSupporter(value: any) {
    if (value && value.Id) {
      this.newSupporterId = value.Id;
      this.warningDisplayed = this.checkIfSupporterHasMeetings();
    } else {
      this.newSupporterId = null;
      this.warningDisplayed = false;
    }

    this.selectedSupporter = value;
  }

  assignCategory(value: any) {
    if (value && value.Id) {
      this.newCategoryId = value.Id;
      this.filterSupportersByCategory(this.newCategoryId);
      this.checkIfCurrentSupporterHasExpertise(this.newCategoryId);
    } else {
      this.newCategoryId = null;
      this.supportersDropdown = [];
    }

    this.selectedCategory = value;
  }

  isTeamsCall(): boolean {
    return this.meeting.Type === MeetingType.Teams;
  }

  getLanguageSrc(): string {
    const lang = this.flags.find(l => l.code === this.translatedMeetingLanguage.LanguageCode.toString().toLowerCase());

    return !!lang ? lang.src : '';
  }

  getFormattedDate(time: any, format: string): string {
    const date = new Date(time + 'Z');
    return formatDate(date, format);
  }

  userInfoExists(): boolean {
    if (!!this.meeting) {
      return !!this.meeting.UserFirstName && this.meeting.UserFirstName.trim().length > 0;
    }

    return false;
  }

  getEmailUrl() {
    const userEmailUrl = 'sip:' + this.meeting.UserEmail;
    window.open(userEmailUrl);
  }

  setMeetingLanguage() {
    this.translatedMeetingLanguage = this.sharedService.translateLanguage(<Language>{
      Id: this.meeting.LanguageId,
      Name: this.meeting.LanguageName
    });
  }

  private setSupporterName() {
    const selectedSupporter = this.supporters.find(s => s.Id === this.newSupporterId);

    if (!!selectedSupporter) {
      this.supporterName =  selectedSupporter.FirstName + ' ' + selectedSupporter.LastName ;
    }
  }

  private setCategoryName() {
    const selectedCategory = this.allCategories.find(c => c.Id === this.newCategoryId);

    if (!!selectedCategory) {
      this.meeting.CategoryName = selectedCategory.Name;
    }
  }

  private checkIfSupporterHasMeetings(): boolean {
    if (this.oldSupporterId === this.newSupporterId) {
      return false;
    }

    const meeting = this.allMeetings.find(m => m.SupporterId === this.newSupporterId && m.Start === this.meeting.Start);
    return !!meeting;
  }

  private setIfMeetingIsEditable() {
    const today = new Date();
    const end = new Date(this.meeting.End + 'Z');
    this.isEditable = end >= today;
  }

  private filterSupportersByCategory(categoryId) {
    this.supportersDropdown = this.supporters.filter(s => s.CategoryIds.indexOf(categoryId) > -1);
  }

  private checkIfCurrentSupporterHasExpertise(categoryId) {
    if (!!this.selectedSupporter && this.selectedSupporter.CategoryIds.indexOf(categoryId) === -1) {
      this.selectedSupporter = undefined;
    }
  }
}
